import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HelpCircle, MessageCircle, Phone, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const QA = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const faqs = [
    {
      question: 'How much does roof coating cost?',
      answer: 'Most Central Florida homes pay $3,000-$7,000 depending on roof size and type. We offer FREE estimates! Call (321) 804-4413 for your free quote.'
    },
    {
      question: 'How long does the coating last?',
      answer: 'Our professional-grade coatings last 10-15 years in Florida\'s harsh climate, backed by our comprehensive warranty.'
    },
    {
      question: 'Do you serve Tampa?',
      answer: 'Yes! We serve Tampa, St. Pete, Clearwater, and all surrounding areas in Central Florida.'
    },
    {
      question: 'Will this really reduce my AC bills?',
      answer: 'Absolutely! Our reflective coatings reduce cooling costs by 20-30%. Most customers see savings on their very first electricity bill.'
    },
    {
      question: 'What types of roofs can you coat?',
      answer: 'We coat all roof types: asphalt shingles, metal roofs, tile roofs, flat roofs, and TPO membranes.'
    },
    {
      question: 'How long does installation take?',
      answer: 'Most residential roofs are completed in just ONE DAY! We work efficiently without sacrificing quality.'
    },
    {
      question: 'Do I need to remove my old roof?',
      answer: 'No! That\'s the beauty of coating - no tear-off needed. We clean and prep your existing roof, then apply the coating.'
    },
    {
      question: 'Are you licensed and insured?',
      answer: 'Yes, we\'re fully licensed and insured in Florida. Family-owned and operated with certified applicators.'
    },
    {
      question: 'Do you offer free inspections?',
      answer: 'Yes! We provide FREE, no-obligation roof inspections and detailed estimates. Call (321) 804-4413 to schedule.'
    },
    {
      question: 'What\'s your warranty?',
      answer: 'We offer comprehensive warranties covering both materials and labor. Specific terms vary by project - we\'ll explain everything during your free inspection.'
    }
  ];

  // FAQ Schema for Google Rich Results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "FAQ",
        "item": "https://savagerejuvenationcoatings.com/qa"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Roof Coating FAQ Orlando Tampa FL | Common Questions | Savage Rejuvenation Coatings</title>
        <meta name="description" content="Get expert answers to common questions about roof coating in Central Florida. Learn about costs, benefits, durability, hurricane protection, and our service areas in Orlando, Tampa, Lakeland, Kissimmee & beyond. Call (321) 804-4413." />
        <meta name="keywords" content="roof coating FAQ, roof coating questions Orlando, how long does roof coating last Florida, roof coating cost Tampa, hurricane proof roof coating FAQ, Central Florida roofing questions" />
        
        <meta property="og:title" content="Roof Coating FAQ | Expert Answers | Savage Rejuvenation Coatings" />
        <meta property="og:description" content="Everything you need to know about roof coating in Central Florida. Expert answers from licensed contractors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://savagerejuvenationcoatings.com/qa" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com/qa" />
        <meta name="robots" content="index, follow" />
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3">
            <div className="flex items-center justify-between">
              <nav className="hidden lg:flex items-center gap-6">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold">Home</a>
                <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold">Reviews</a>
                <a href="/qa" className="text-primary font-manrope font-semibold">Q&A</a>
                <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold">Blog</a>
              </nav>
              <div className="hidden lg:flex items-center gap-6">
                <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </div>
              <div className="lg:hidden flex items-center gap-3">
                <a href="tel:3218044413">
                  <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                    <Phone className="h-5 w-5" />
                  </Button>
                </a>
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-white p-2"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t border-gray-600 pt-4 flex flex-col gap-4">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold text-lg">Home</a>
                <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold text-lg">Reviews</a>
                <a href="/qa" className="text-primary font-manrope font-semibold text-lg">Q&A</a>
                <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold text-lg">Blog</a>
                <a href="tel:3218044413" className="text-primary font-manrope font-semibold text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </nav>
            )}
          </div>
        </header>

        <section className="py-20 bg-gradient-to-b from-accent-light to-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <HelpCircle className="h-16 w-16 text-primary mx-auto mb-4" />
              <h1 className="font-barlow text-5xl font-bold uppercase mb-4">Frequently Asked Questions</h1>
              <p className="text-xl font-manrope text-gray-700">Everything you need to know about roof coating in Central Florida</p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000] p-6">
                  <h3 className="font-barlow text-xl font-bold uppercase mb-3 flex items-start gap-3">
                    <MessageCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    {faq.question}
                  </h3>
                  <p className="font-manrope text-gray-700 ml-9">{faq.answer}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-primary border-2 border-black shadow-[6px_6px_0px_0px_#000] p-8 text-white text-center">
              <h2 className="font-barlow text-3xl font-bold uppercase mb-4">Still Have Questions?</h2>
              <p className="text-lg font-manrope mb-6">We're here to help! Give us a call for personalized answers.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:3218044413" className="bg-white hover:bg-gray-100 text-black px-8 py-4 border-2 border-black shadow-[4px_4px_0px_0px_#000] font-barlow uppercase font-bold inline-block">
                  Call: (321) 804-4413
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default QA;